document.addEventListener("DOMContentLoaded", () => {
  const pendingImpressions = [];
  const uniqueKeys = new Set();
  const observedElements = new WeakSet();
  let flushTimer = null;

  // --- Intersection Observer (single instance, never torn down) ---

  const observer = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          const element = entry.target;

          const impressionData = {
            record_id: element.dataset.id,
            record_type: element.dataset.type,
            viewed: true,
            viewed_at: new Date().toISOString(),
            device_type: getDeviceType(),
            device_os: getDeviceOS(),
            device_screen_size: getDeviceScreenSize(),
            page_or_screen: window.location.pathname,
          };

          const impressionKey = `${impressionData.record_id}-${impressionData.record_type}-${window.location.pathname}`;

          if (!uniqueKeys.has(impressionKey)) {
            uniqueKeys.add(impressionKey);
            pendingImpressions.push(impressionData);
            scheduleFlush();
          }

          observer.unobserve(element);
        }
      });
    },
    {
      root: null,
      rootMargin: "0px",
      threshold: 0.5,
    }
  );

  // --- Observe .track-impression elements (idempotent via WeakSet) ---

  function observeNewElements(root) {
    const elements = (root || document).querySelectorAll(".track-impression");
    elements.forEach((el) => {
      if (!observedElements.has(el)) {
        observedElements.add(el);
        observer.observe(el);
      }
    });
  }

  // Initial scan
  observeNewElements();

  // Watch for dynamically added elements — only observe new ones
  const mutationObserver = new MutationObserver((mutations) => {
    for (const mutation of mutations) {
      for (const node of mutation.addedNodes) {
        if (node.nodeType !== Node.ELEMENT_NODE) continue;
        if (node.classList && node.classList.contains("track-impression")) {
          if (!observedElements.has(node)) {
            observedElements.add(node);
            observer.observe(node);
          }
        }
        // Also check children of the added node
        if (node.querySelectorAll) {
          observeNewElements(node);
        }
      }
    }
  });

  mutationObserver.observe(document.body, {
    childList: true,
    subtree: true,
  });

  // --- Debounced batch send (200ms) ---

  function scheduleFlush() {
    if (flushTimer) return;
    flushTimer = setTimeout(() => {
      flushTimer = null;
      if (pendingImpressions.length === 0) return;

      const batch = pendingImpressions.splice(0, pendingImpressions.length);
      fetch("/api/services/v1/impressions/create", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ impressions: batch }),
      }).catch((err) => console.error("Failed to send impressions:", err));
    }, 200);
  }

  // --- Event delegation for engagement clicks ---
  // Auto-tracks clicks on any link inside .track-impression elements.
  // No manual onclick handlers needed in views.

  document.addEventListener("click", (e) => {
    const trackParent = e.target.closest(".track-impression");
    if (!trackParent) return;

    const link = e.target.closest("a");
    if (!link) return;

    if (trackParent.dataset.id && trackParent.dataset.type) {
      sendUserEngagement(trackParent);
    }
  }, true);

  // --- Engagement sender ---

  function sendUserEngagement(element) {
    const engagementData = {
      record_id: element.dataset.id,
      record_type: element.dataset.type,
      viewed_at: new Date().toISOString(),
      device_type: getDeviceType(),
      device_os: getDeviceOS(),
      device_screen_size: getDeviceScreenSize(),
      page_or_screen: window.location.pathname,
      user_engagement: true,
    };

    fetch("/api/services/v1/impressions/create", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ impressions: [engagementData] }),
    }).catch((err) => console.error("Failed to send user engagement:", err));
  }

  // --- Device helpers ---

  function getDeviceType() {
    if (window.innerWidth <= 768) return "mobile phones";
    if (window.innerWidth <= 1024) return "tablets";
    return "desktop";
  }

  function getDeviceOS() {
    const userAgent = navigator.userAgent.toLowerCase();
    if (userAgent.includes("windows")) return "Windows";
    if (userAgent.includes("mac os")) return "macOS";
    if (userAgent.includes("android")) return "Android";
    if (userAgent.includes("iphone") || userAgent.includes("ipad")) return "iOS";
    return "unknown";
  }

  function getDeviceScreenSize() {
    return `${window.innerWidth}x${window.innerHeight}`;
  }
});
